import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { apiGet, apiSend } from '@/lib/api';
import {
  FLIGHT_TYPES, FLIGHT_TYPE_MAP, SOULS_FIELDS, SOUL_AFFILIATIONS,
  affiliationLabel, soulRoleLabel, isExcludedAffiliation,
  FlightTypeCode, SoulAffiliationCode, SoulRoleCode,
} from '@/lib/flightTypes';
import { ManifestRowFields, ExcludedChip, type ManifestEntry } from '@/components/ManifestRow';
import { buildFlightPatch, flightTimeProblem } from '@/lib/flightPatch';
import { distanceLabel, placeLabel, type RouteFacts } from '@/lib/summary';
import { Card, Spinner, ErrorNote, TypeBadge, Field, inputClass } from '@/components/ui';
import AircraftSilhouette from '@/components/AircraftSilhouette';

interface Soul {
  id: string; initials: string; full_name: string; relationship?: string | null;
  category: FlightTypeCode; role_aboard: SoulRoleCode; affiliation: SoulAffiliationCode;
}
/** BA Law's columns G–M plus their locked column F, derived by the API. */
interface Headcount extends Record<string, unknown> {
  souls_on_board: number;
  zero_reason: string | null;
  stale: boolean;
}
interface Flight extends Partial<RouteFacts> {
  id: string; date: string; departure_airport: string; arrival_airport: string;
  /** Flight time as entered; `null` on a leg logged before the basis changed. */
  flight_hours: number | string | null; flight_type: FlightTypeCode; operator?: string | null;
  notes?: string | null;
  souls?: Soul[]; headcount?: Headcount;
  /** FlightAware's airborne time for this leg — offered as the prefill, never stored. */
  measured_airborne?: { flightaware_id: string; hours: number } | null;
}

function toLocalInput(iso: string) {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function deductibility(type: string) {
  if (['B', 'T', 'M'].includes(type))
    return { text: '100% deductible', cls: 'text-emerald-300', chip: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30' };
  if (type === 'PNE')
    return { text: 'May be deductible', cls: 'text-amber-300', chip: 'bg-amber-500/10 text-amber-300 ring-amber-500/30' };
  return { text: 'Not deductible', cls: 'text-slate-400', chip: 'bg-slate-500/10 text-slate-400 ring-slate-500/30' };
}

export default function FlightDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const warning = (location.state as { warning?: string } | null)?.warning;
  const [flight, setFlight] = useState<Flight | null>(null);
  const [souls, setSouls] = useState<Soul[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<Partial<Flight>>({});
  const [newSoul, setNewSoul] = useState<ManifestEntry>({
    initials: '', full_name: '', relationship: '', category: 'B', role_aboard: 'passenger', affiliation: 'guest',
  });
  const [saveNew, setSaveNew] = useState(false);
  const [roster, setRoster] = useState<Array<{ id: string; full_name: string; initials: string | null; relationship: string | null }>>([]);

  useEffect(() => {
    apiGet<{ roster: typeof roster }>('/api/roster').then((d) => setRoster(d.roster || [])).catch(() => {});
  }, []);

  function load() {
    setLoading(true);
    apiGet<Flight>(`/api/flights/${id}`)
      .then((f) => {
        setFlight(f);
        setSouls(f.souls || []);
        setNewSoul((n) => ({ ...n, category: f.flight_type }));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, [id]);

  /** Open the edit form. A leg with no flight time yet starts from FlightAware's figure. */
  function startEdit(f: Flight) {
    const prefill = f.flight_hours == null && f.measured_airborne ? f.measured_airborne.hours : f.flight_hours;
    setEdit({ ...f, flight_hours: prefill });
    setEditing(true);
  }

  async function saveEdit() {
    setError('');
    // A leg is not saved without a flight time — an edit to one that has none yet
    // included. That is how the legs logged before the basis changed get theirs.
    const problem = flightTimeProblem(edit.flight_hours);
    if (problem) return setError(problem);
    try {
      await apiSend(`/api/flights/${id}`, 'PATCH', buildFlightPatch(edit));
      setEditing(false);
      load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
  }

  async function addSoul(person?: { full_name: string; initials?: string | null; relationship?: string | null }) {
    const src: ManifestEntry = person
      ? {
          initials: person.initials || '',
          full_name: person.full_name,
          relationship: person.relationship || '',
          category: flight?.flight_type || 'B',
          role_aboard: 'passenger',
          affiliation: 'guest',
        }
      : newSoul;
    if (!src.full_name && !src.initials) return;
    const initials = src.initials || src.full_name.split(' ').map((s) => s[0]).join('').toUpperCase();
    try {
      await apiSend(`/api/flights/${id}/souls`, 'POST', {
        initials,
        full_name: src.full_name || initials,
        relationship: src.relationship || undefined,
        category: src.category,
        role_aboard: src.role_aboard,
        affiliation: src.affiliation,
      });
      if (!person && saveNew && newSoul.full_name) {
        await apiSend('/api/roster', 'POST', { full_name: newSoul.full_name, initials, relationship: newSoul.relationship || undefined }).catch(() => {});
        apiGet<{ roster: typeof roster }>('/api/roster').then((d) => setRoster(d.roster || [])).catch(() => {});
      }
      if (!person) {
        setNewSoul({ initials: '', full_name: '', relationship: '', category: flight?.flight_type || 'B', role_aboard: 'passenger', affiliation: 'guest' });
        setSaveNew(false);
      }
      load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not add that soul'); }
  }

  /** One field on one soul. The API recounts; the page re-reads what it derived. */
  async function updateSoul(soulId: string, patch: Partial<ManifestEntry>) {
    try {
      await apiSend(`/api/souls/${soulId}`, 'PATCH', patch);
      load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not update the manifest'); }
  }

  async function removeSoul(soulId: string) {
    try { await apiSend(`/api/souls/${soulId}`, 'DELETE'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not remove them from the manifest'); }
  }

  async function deleteFlight() {
    if (!confirm('Delete this flight and its manifest? This cannot be undone.')) return;
    try { await apiSend(`/api/flights/${id}`, 'DELETE'); navigate('/flights'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Delete failed'); }
  }

  if (loading) return <div className="p-8"><Spinner /></div>;
  if (error && !flight) return <div className="mx-auto max-w-3xl p-8"><ErrorNote message={error} /></div>;
  if (!flight) return null;

  const ded = deductibility(flight.flight_type);
  const fmt = new Date(flight.date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
      <Link to="/flights" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200">← Flight Log</Link>

      {/* Hero header */}
      <Card className="relative mb-6 overflow-hidden p-6">
        <AircraftSilhouette className="pointer-events-none absolute -right-8 top-1/2 h-[190%] -translate-y-1/2 rotate-[20deg] text-avio/[0.05]" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs text-slate-500">{fmt}</p>
            <h1 className="ident mt-1 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {flight.departure_airport} → {flight.arrival_airport}
            </h1>
            {placeLabel(flight.destination) && (
              <p className="mt-1 text-sm text-slate-400">To {placeLabel(flight.destination)}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <TypeBadge code={flight.flight_type} />
              {flight.flight_hours != null ? (
                <span className="tabular-nums text-sm text-slate-300">{Number(flight.flight_hours).toFixed(2)} h</span>
              ) : (
                <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200 ring-1 ring-inset ring-amber-500/30">no flight time</span>
              )}
              <span className="tabular-nums text-sm text-slate-400" title="Great-circle distance between the two airports">{distanceLabel(flight as RouteFacts)}</span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-slate-300 ring-1 ring-inset ring-white/10"
                title="Pilot in command — from the manifest, the only place it is recorded">
                PIC {souls.find((p) => p.role_aboard === 'PIC')?.initials || '—'}
              </span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-slate-300 ring-1 ring-inset ring-white/10"
                title="Souls on board — BA Law's headcount for this leg">
                {flight.headcount?.souls_on_board ?? 0} {flight.headcount?.souls_on_board === 1 ? 'soul' : 'souls'}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${ded.chip}`}>
                {ded.text}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!editing && <button onClick={() => startEdit(flight)} className="btn-ghost text-sm">Edit</button>}
            <button onClick={deleteFlight} className="btn-ghost text-sm text-red-300 hover:bg-red-500/10">Delete</button>
          </div>
        </div>
      </Card>

      {warning && <div className="mb-4"><ErrorNote message={warning} /></div>}
      {flight.flight_hours == null && !editing && (
        <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-3 text-sm text-amber-200">
          This leg has no flight time yet, so it is missing from every total and its BA Law row is blank.{' '}
          {flight.measured_airborne
            ? `FlightAware measured ${flight.measured_airborne.hours.toFixed(2)} h airborne — `
            : 'FlightAware has no measured figure for it — '}
          <button onClick={() => startEdit(flight)} className="underline underline-offset-2">enter it</button>.
        </div>
      )}
      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      {/* Details */}
      <Card className="p-5 sm:p-6">
        {editing ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date & time">
                <input type="datetime-local" defaultValue={toLocalInput(flight.date)}
                  onChange={(e) => setEdit((s) => ({ ...s, date: e.target.value }))} className={inputClass} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Departure">
                  <input maxLength={4} defaultValue={flight.departure_airport}
                    onChange={(e) => setEdit((s) => ({ ...s, departure_airport: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} className={`${inputClass} ident`} />
                </Field>
                <Field label="Arrival">
                  <input maxLength={4} defaultValue={flight.arrival_airport}
                    onChange={(e) => setEdit((s) => ({ ...s, arrival_airport: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} className={`${inputClass} ident`} />
                </Field>
              </div>
              <div>
                <Field label="Flight time (hrs)" hint="Required — a leg cannot be saved without one">
                  <input required type="number" step="0.01" min="0.01" max="24" inputMode="decimal"
                    defaultValue={edit.flight_hours == null ? '' : String(edit.flight_hours)}
                    onChange={(e) => setEdit((s) => ({ ...s, flight_hours: e.target.value }))} className={inputClass} />
                </Field>
                {flight.measured_airborne && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    {flight.flight_hours == null ? 'Filled in from ' : ''}FlightAware's measured airborne time:{' '}
                    <span className="tabular-nums text-slate-400">{flight.measured_airborne.hours.toFixed(2)} h</span>
                    {flight.flight_hours == null ? '. Change it to what you flew.' : ' (wheels-up → wheels-down).'}
                  </p>
                )}
              </div>
              <Field label="Flight type">
                <select defaultValue={flight.flight_type} onChange={(e) => setEdit((s) => ({ ...s, flight_type: e.target.value as FlightTypeCode }))} className={inputClass}>
                  {FLIGHT_TYPES.map((t) => <option key={t.code} value={t.code} className="bg-night-800">{t.code} · {t.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Operator">
              <input defaultValue={flight.operator || ''} onChange={(e) => setEdit((s) => ({ ...s, operator: e.target.value }))} className={inputClass} />
            </Field>
            <Field label="Notes">
              <textarea rows={3} defaultValue={flight.notes || ''} onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))} className={inputClass} />
            </Field>
            <div className="flex gap-2">
              <button onClick={saveEdit} className="btn-primary text-sm">Save</button>
              <button onClick={() => setEditing(false)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </div>
        ) : (
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Type</dt><dd className="mt-1"><TypeBadge code={flight.flight_type} /> <span className="ml-2 text-sm text-slate-400">{FLIGHT_TYPE_MAP[flight.flight_type]?.label}</span></dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Flight time</dt><dd className="mt-1 tabular-nums text-white">{flight.flight_hours != null ? `${Number(flight.flight_hours).toFixed(2)} h` : <span className="text-amber-200">not entered</span>}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Destination</dt><dd className="mt-1 text-slate-200">{placeLabel(flight.destination) ?? <span className="text-slate-500">{flight.arrival_airport} — not in the airport table</span>}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Distance</dt><dd className="mt-1 tabular-nums text-slate-200" title="Great-circle distance between the two airports, computed from their codes">{distanceLabel(flight as RouteFacts)}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Pilot in command</dt><dd className="mt-1 text-slate-200">{souls.find((p) => p.role_aboard === 'PIC')?.full_name || '—'}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Operator</dt><dd className="mt-1 text-slate-200">{flight.operator || '—'}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Deductibility</dt><dd className={`mt-1 ${ded.cls}`}>{ded.text}</dd></div>
            <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-slate-500">Notes</dt><dd className="mt-1 whitespace-pre-wrap text-slate-300">{flight.notes || '—'}</dd></div>
          </dl>
        )}
      </Card>

      {/* Souls on board */}
      <Card className="mt-6 p-5 sm:p-6">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Souls on board</h2>
          <p className="text-xs text-slate-500">
            Everyone physically aboard. The headcount below is derived from this list, not typed.
          </p>
        </div>

        {flight.headcount && (
          <div className="mb-4 rounded-lg border border-white/5 bg-white/[0.02] p-3">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-sm text-slate-400">Headcount for BA Law</span>
              <span className={`tabular-nums text-lg font-semibold ${flight.headcount.souls_on_board === 0 ? 'text-red-300' : 'text-white'}`}>
                {flight.headcount.souls_on_board}
              </span>
              {SOULS_FIELDS.filter((f) => Number(flight.headcount?.[f.field] ?? 0) > 0).map((f) => (
                <span key={f.code} className="text-xs text-slate-400">
                  {f.code} <span className="tabular-nums text-slate-200">{String(flight.headcount?.[f.field])}</span>
                </span>
              ))}
            </div>
            {flight.headcount.zero_reason && (
              <p className="mt-2 text-xs text-amber-200">{flight.headcount.zero_reason}</p>
            )}
            {flight.headcount.stale && (
              <p className="mt-2 text-xs text-red-300">
                The stored headcount disagrees with this manifest. Re-save the flight to rebuild it.
              </p>
            )}
          </div>
        )}

        {souls.length === 0 ? (
          <p className="text-sm text-red-300">Nobody recorded aboard — the aeroplane does not fly itself.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {souls.map((p) => (
              <li key={p.id} className="-mx-2 rounded-lg px-2 py-3 transition hover:bg-white/[0.03]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-xs font-semibold text-slate-300">{p.initials}</span>
                    <div>
                      <p className="flex items-center gap-2 text-sm text-white">
                        {p.full_name || p.initials}
                        <ExcludedChip affiliation={p.affiliation} />
                      </p>
                      <p className="text-xs text-slate-500">
                        {soulRoleLabel(p.role_aboard)} · {affiliationLabel(p.affiliation)}
                        {p.relationship ? ` · ${p.relationship}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select aria-label={`Relationship to the operation for ${p.full_name || p.initials}`}
                      value={p.affiliation}
                      onChange={(e) => updateSoul(p.id, { affiliation: e.target.value as SoulAffiliationCode })}
                      className={`${inputClass} max-w-[15rem] text-xs`}>
                      {SOUL_AFFILIATIONS.map((a) => (
                        <option key={a.code} value={a.code} className="bg-night-800">
                          {a.label}{a.excluded ? ' — not counted' : ''}
                        </option>
                      ))}
                    </select>
                    <TypeBadge code={p.category} />
                    <button onClick={() => removeSoul(p.id)} className="text-slate-500 hover:text-red-300" aria-label="Remove from manifest">✕</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* BA Law asks that instructors and professional pilots aboard be named
            in the leg's Notes. Excluded from the count is not excluded from the record. */}
        {souls.some((p) => isExcludedAffiliation(p.affiliation)) && !flight.notes && (
          <p className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-3 text-xs text-amber-200">
            {souls.filter((p) => isExcludedAffiliation(p.affiliation)).map((p) => p.full_name || p.initials).join(', ')} —
            aboard but outside the headcount. BA Law asks for them in the Notes for this leg.
          </p>
        )}

        {/* Add a soul */}
        <div className="mt-4 border-t border-white/5 pt-4">
          {roster.filter((s) => !souls.some((p) => p.full_name === s.full_name)).length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5 text-xs text-slate-500">Quick add from roster:</p>
              <div className="flex flex-wrap gap-2">
                {roster.filter((s) => !souls.some((p) => p.full_name === s.full_name)).map((s) => (
                  <button key={s.id} onClick={() => addSoul(s)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300 ring-1 ring-white/10 transition hover:bg-avio/15 hover:text-white">
                    <span className="grid h-4 w-4 place-items-center rounded-full bg-avio/20 text-[9px] font-semibold text-avio">{s.initials || s.full_name[0]}</span>
                    {s.full_name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <ManifestRowFields row={newSoul} onChange={(k, v) => setNewSoul((s) => ({ ...s, [k]: v }))} />
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <button onClick={() => addSoul()} className="btn-ghost text-sm">Add to manifest</button>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)} className="accent-avio" />
              Save to roster for next time
            </label>
          </div>
        </div>
      </Card>
    </div>
  );
}
